import dayjs from 'dayjs'
import PropTypes from 'prop-types'
import pluralize from 'pluralize'
import {
  Box,
  Button,
  CloseButton,
  Flex,
  Text,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
} from '@chakra-ui/react'
import {ExternalLinkIcon} from '@chakra-ui/icons'
import {useTranslation} from 'react-i18next'
import InvoiceOwing from './InvoiceOwing'
import InvoiceStatusTag from './InvoiceStatusTag'
import InvoiceDetailBlock from './InvoiceDetailBlock'
import {toLocalDate} from '../../../../../utils/formatter'
import {openTab} from '../../../../../utils/browser'

const InvoiceDrawer = ({isOpen, onClose, invoice}) => {
  const {t} = useTranslation('payerStatements')

  if (!invoice) {
    return null
  }

  const paymentTerms = dayjs(invoice?.dueDate || 0).diff(
    invoice?.issueDate || 0,
    'day',
  )

  const issueDate = toLocalDate(invoice?.issueDate, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })

  const dueDate = toLocalDate(invoice?.dueDate, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })

  return (
    <Drawer isOpen={isOpen} placement="right" size="md" onClose={onClose}>
      <DrawerOverlay />

      <DrawerContent>
        <DrawerHeader>
          <Flex
            alignItems="center"
            justifyContent="space-between"
            py="xs"
            textTransform="uppercase"
          >
            <Text textStyle="body-small">{invoice?.number || ''}</Text>

            <Flex alignItems="center" gap={5}>
              <InvoiceStatusTag
                status={invoice?.status || ''}
                daysPastDue={invoice?.dpd || 0}
                displayDaysPastDue={false}
              />
              <CloseButton onClick={onClose} />
            </Flex>
          </Flex>
        </DrawerHeader>
        <DrawerBody>
          <Flex py="xs" mt="xs" mb="md" justifyContent="space-between">
            <Box w="100%" alignSelf="flex-end" textStyle="body-small">
              <Flex pt="md" pb="sm" alignItems="center" gap={5}>
                <Text textTransform="uppercase">
                  {t('invoices.drawer.title.invoiceStatus')}
                </Text>
                <InvoiceStatusTag
                  status={invoice?.status || ''}
                  daysPastDue={invoice?.dpd || 0}
                  displayDaysPastDue
                />
              </Flex>
              {invoice?.dueAmount && invoice?.paymentUrl && (
                <InvoiceOwing
                  dueAmount={invoice?.dueAmount || {}}
                  buttonText={t('invoices.drawer.button.payNow')}
                  paymentUrl={invoice?.paymentUrl || ''}
                />
              )}
            </Box>
          </Flex>
          <Box pt="md" pb="sm">
            <Flex justifyContent="space-between">
              <InvoiceDetailBlock
                header={t('invoices.drawer.invoiceDetails.issueDate')}
                value={issueDate}
              />
              <InvoiceDetailBlock
                header={t('invoices.drawer.invoiceDetails.dueDate')}
                value={dueDate}
              />
            </Flex>
          </Box>
          <Box pb="sm">
            <Flex justifyContent="space-between">
              <InvoiceDetailBlock
                header={t('invoices.drawer.invoiceDetails.invoiceNumber')}
                value={invoice?.number?.toUpperCase() || ''}
              />
              <InvoiceDetailBlock
                header={t('invoices.drawer.invoiceDetails.paymentTerm')}
                value={`${paymentTerms} ${pluralize('day', paymentTerms)}`}
              />
            </Flex>
          </Box>
          <Button
            variant="outline"
            colorScheme="grey"
            borderRadius="full"
            size="md"
            mt="sm"
            rightIcon={<ExternalLinkIcon />}
            textTransform="uppercase"
            onClick={openTab(invoice?.pdfUrl)}
          >
            {t('invoices.drawer.button.viewInvoice')}
          </Button>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  )
}

InvoiceDrawer.defaultProps = {
  invoice: null,
}

InvoiceDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  invoice: PropTypes.oneOfType([
    PropTypes.shape({}),
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      number: PropTypes.string.isRequired,
      dpd: PropTypes.number.isRequired,
      issueDate: PropTypes.string.isRequired,
      dueDate: PropTypes.string.isRequired,
      dueAmount: PropTypes.shape({
        toFormat: PropTypes.func.isRequired,
      }).isRequired,
      status: PropTypes.string.isRequired,
      paymentUrl: PropTypes.string.isRequired,
      pdfUrl: PropTypes.string.isRequired,
    }),
  ]),
}

export default InvoiceDrawer
